import React, { useMemo } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { AnimatedNumberText, ButtonFluid, theme, useResponsive } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationCTA = (props: CustomizationCTAProps) => {
  const { variation, variant = 'legacy', cost, calories, quantity, disabled, onPress } = props;

  const { formatNumber, formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();

  // ─── Derived Data ────────────────────────────────────────────────────

  const formattedCost = useMemo(() => {
    const formattedValue = formatNumber(cost / 100, { style: 'currency', currency: 'USD' });

    if (quantity > 1) {
      return formatMessage(messages.costPerProduct, { cost: formattedValue });
    }

    return formattedValue;
  }, [formatNumber, cost, quantity, formatMessage]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const pressableVariantStyle = useMemo(() => {
    if (variant === 'legacy') {
      return styles.pressableLegacy;
    }

    if (currentBreakpoint.isXS) {
      return styles.pressableNewXS;
    }

    return styles.pressableNewSM;
  }, [currentBreakpoint.isXS, variant]);

  const buttonTextStyle = useMemo(() => {
    if (variant === 'new' && !disabled) {
      return styles.buttonTextNew;
    }
  }, [disabled, variant]);

  const costTextStyle = useMemo(() => {
    if (disabled) {
      return styles.costTextDisabled;
    }

    if (variant === 'new') {
      return styles.buttonTextNew;
    }

    return styles.costText;
  }, [disabled, variant]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ButtonFluid.Pressable
      style={[styles.pressableBase, pressableVariantStyle]}
      disabled={disabled}
      onPress={onPress}
    >
      <ButtonFluid.Label disabled={disabled} sizeMatch={['18']} style={buttonTextStyle}>
        {variation === 'continue' ? <FormattedMessage {...messages.continueLabel} /> : null}
        {variation === 'add-to-bag' ? <FormattedMessage {...messages.addToBagLabel} /> : null}
        {variation === 'update' ? <FormattedMessage {...messages.updateLabel} /> : null}
      </ButtonFluid.Label>

      <View style={styles.textContainer}>
        {typeof calories === 'number' ? (
          <ButtonFluid.Text disabled={disabled} sizeMatch={['16']} style={buttonTextStyle}>
            <FormattedMessage {...messages.calories} values={{ calories }} />
          </ButtonFluid.Text>
        ) : null}

        <AnimatedNumberText.BodyText
          text={formattedCost}
          animateInitialValue={false}
          duration={PRICE_ANIMATION_DURATION}
          sizeMatch={['16']}
          style={costTextStyle}
        />
      </View>
    </ButtonFluid.Pressable>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PRICE_ANIMATION_DURATION = 1_000;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  continueLabel: {
    defaultMessage: 'Continue',
    description: 'Customization | CTA | "Continue" label',
  },
  addToBagLabel: {
    defaultMessage: 'Add to bag',
    description: 'Customization | CTA | "Add to bag" label',
  },
  updateLabel: {
    defaultMessage: 'Update',
    description: 'Customization | CTA | "Update" label',
  },
  calories: {
    defaultMessage: '{calories} cal',
    description: 'Customization | CTA | Calories',
  },
  costPerProduct: {
    defaultMessage: '{cost}/ea',
    description: 'Customization | CTA | Cost per product',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pressableBase: {
    borderRadius: theme.radius['3xLarge'],
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 0, // override default style
    paddingTop: 0, // override default style
    paddingBottom: 0, // override default style
  },
  pressableLegacy: {
    minWidth: 287,
    minHeight: 64,
    maxHeight: 64,
    paddingHorizontal: theme.spacing['8'],
    ...theme.elevations[6],
  },
  pressableNewXS: {
    boxShadow: '0px 0px 20px 0px rgba(14, 21, 14, 0.05)',
    width: 'auto', // override default style
    minWidth: 215,
    minHeight: 56,
    maxHeight: 56,
    gap: theme.spacing['6'],
    paddingHorizontal: theme.spacing['6'],
  },
  pressableNewSM: {
    boxShadow: '0px 0px 20px 0px rgba(14, 21, 14, 0.05)',
    minWidth: 287,
    minHeight: 72,
    maxHeight: 72,
    gap: theme.spacing['6'],
    paddingHorizontal: theme.spacing['8'],
  },
  buttonTextNew: {
    color: theme.colors.OATMEAL,
  },
  textContainer: {
    flexDirection: 'row',
    alignItems: 'baseline',
    columnGap: theme.spacing['3'],
  },
  costText: {
    color: theme.colors.LIME,
  },
  costTextDisabled: {
    color: theme.colors.OPACITY.DARK_KALE.DARK,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationCTAProps = {
  variation: 'add-to-bag' | 'continue' | 'update';
  variant?: 'legacy' | 'new';
  cost: number;
  calories?: number;
  quantity: number;
  disabled?: boolean;
  onPress: () => void;
};
